import { endpoints } from "@/api/endpoints/endpoints";
import {
  bulkUpdateProductPrices as bulkUpdateProductPricesApi,
  createProduct as createProductApi,
  deleteProduct as deleteProductApi,
  getProduct,
  getProducts,
  getProductsWithRelations,
  getProductVariants,
  getProductWithRelation,
  searchProductsWithPagination,
  searchProductVariantsWithPagination,
  updateProduct as updateProductApi,
  updateProductPrice as updateProductPriceApi
} from "@/api/product-api";
import {
  buildCacheKey,
  useCollectionData,
  useCollectionData as useCollectionResponse,
  useMutation,
  usePaginatedData,
} from "@/hooks/use-api";
import { toast } from "@/hooks/use-toast";
import { ApiResponse } from "@/types/api-response";
import {
  FilterRequest,
  Product,
  ProductFilters,
  ProductFormValues,
  ProductSortOption,
  TProductWithRelation,
  VariantStockSearchItem,
  VariantWithRelationResponse
} from "@/types/product-type";
import { useLocation } from "wouter";
import { mutate } from "swr";

// Constants
const PRODUCTS_ENDPOINT = "/products";

// Hook for getting paginated products
export function useProducts(params?: {
  page?: number;
  size?: number;
  searchTerm?: string;
  sort?: ProductSortOption;
  filters?: ProductFilters;
}) {
  const cacheKey = buildCacheKey(PRODUCTS_ENDPOINT, params);

  return usePaginatedData(cacheKey, () => getProducts(params), {
    refreshInterval: 30000,
    revalidateOnFocus: true,
  });
}

// Hook for getting products with relations (variants and attributes)
export function useProductsWithRelations(params?: {
  page?: number;
  size?: number;
  searchTerm?: string;
  sort?: ProductSortOption;
  enabled?: boolean;
}) {
  const { enabled = true, ...queryParams } = params || {};
  const cacheKey = enabled
    ? buildCacheKey(PRODUCTS_ENDPOINT, queryParams)
    : null;

  return useCollectionResponse<TProductWithRelation[]>(
    cacheKey,
    () => getProductsWithRelations(),
    {
      refreshInterval: 30000,
      revalidateOnFocus: true,
    }
  );
}

// Hook for getting a single product
export function useGetProduct(id: string | null) {
  const cacheKey = id ? endpoints.product.get(id) : null;

  return useCollectionData<Product | null>(
    cacheKey,
    id ? () => getProduct(id) : () => Promise.resolve({ data: null } as any),
    {
      revalidateOnFocus: false,
    }
  );
}

export function useProductWithRelation(id: string | null | undefined) {
  const cacheKey = id ? `${PRODUCTS_ENDPOINT}/${id}/relation` : null;

  return useCollectionResponse<TProductWithRelation>(
    cacheKey,
    id
      ? () => getProductWithRelation(id)
      : () => Promise.resolve({ data: null } as any),
    {
      revalidateOnFocus: false,
    }
  );
}

// Hook for creating a product
export function useCreateProduct() {
  const [, setLocation] = useLocation();
  return useMutation(
    async (data: ProductFormValues) => {
      const response = await createProductApi(data);
      return response;
    },
    {
      onSuccess: (response) => {
        setLocation(`/products/${response.data.id}/edit`);
        toast({
          title: response.statusText,
          description: response.message,
        });
      },
      onError: (error) => {
        toast({
          title: error.statusText,
          description: error.message,
          variant: "destructive",
        });
      },
      invalidateKeys: [PRODUCTS_ENDPOINT],
    }
  );
}

// Hook for updating a product
export function useUpdateProduct() {
  return useMutation(
    async ({ id, data }: { id: string; data: ProductFormValues }) => {
      const response = await updateProductApi(id, data);
      return response;
    },
    {
      onSuccess: (response) => {
        toast({
          title: response.statusText,
          description: response.message,
        });
      },
      onError: (error) => {
        toast({
          title: error.statusText,
          description: error.message,
          variant: "destructive",
        });
      },
      invalidateKeys: [PRODUCTS_ENDPOINT],
    }
  );
}

// Hook for deleting a product
export function useDeleteProduct() {
  return useMutation(deleteProductApi, {
    onSuccess: async (response) => {
      toast({
        title: response.statusText || "Product deleted",
        description:
          response.message || "The product has been deleted successfully.",
      });

      // Invalidate all keys matching /products/search* pattern
      // This ensures the product tree table refreshes after deletion
      await mutate(
        (key) => typeof key === 'string' && key.startsWith('/products/search'),
        undefined,
        { revalidate: true }
      );
    },
    onError: (error) => {
      toast({
        title: error?.statusText || "Delete failed",
        description:
          error?.message || "Failed to delete product. Please try again.",
        variant: "destructive",
      });
    },
    invalidateKeys: [PRODUCTS_ENDPOINT],
  });
}

// Hook for updating product price
export function useUpdateProductPrice() {
  return useMutation(
    ({ productId, price }: { productId: string; price: string }) =>
      updateProductPriceApi(productId, price),
    {
      onSuccess: (response) => {
        console.log("Product price updated successfully:", response.data);
      },
      onError: (error) => {
        console.error("Error updating product price:", error);
      },
      invalidateKeys: [PRODUCTS_ENDPOINT],
    }
  );
}

// Hook for bulk updating product prices
export function useBulkUpdateProductPrices() {
  return useMutation(bulkUpdateProductPricesApi, {
    onSuccess: (response) => {
      console.log("Products bulk updated successfully:", response.data);
    },
    onError: (error) => {
      console.error("Error bulk updating products:", error);
    },
    invalidateKeys: [PRODUCTS_ENDPOINT],
  });
}


// Hook for searching products with pagination request
export function useSearchProducts(request: FilterRequest | null) {
  // Build cache key with proper serialization of FilterRequest
  const cacheKey = request
    ? (() => {
      // Serialize FilterRequest properly for cache key
      const cacheParams: Record<string, any> = {
        filterText: request.filterText,
        page: request.page,
        size: request.size,
        sortBy: request.sortBy,
        ordinal: request.ordinal,
      };

      if (request.searchProp && request.searchProp.length > 0) {
        cacheParams.searchProp = JSON.stringify(request.searchProp);
      }

      return buildCacheKey("/products/search", cacheParams);
    })()
    : null;

  return usePaginatedData<VariantStockSearchItem>(
    cacheKey,
    request
      ? () => searchProductsWithPagination(request)
      : () => Promise.resolve({ data: { content: [] } } as any),
    {
      refreshInterval: 0,
      revalidateOnFocus: false,
    }
  );
}

// Hook for searching product variants with pagination request
export function useSearchProductVariants(request: FilterRequest | null) {
  // Build cache key with proper serialization of FilterRequest
  const cacheKey = request
    ? (() => {
      // Serialize FilterRequest properly for cache key
      const cacheParams: Record<string, any> = {
        filterText: request.filterText,
        page: request.page,
        size: request.size,
        sortBy: request.sortBy,
        ordinal: request.ordinal,
      };

      if (request.searchProp && request.searchProp.length > 0) {
        cacheParams.searchProp = JSON.stringify(request.searchProp);
      }

      return buildCacheKey("/product-variants/search", cacheParams);
    })()
    : null;

  return usePaginatedData<VariantStockSearchItem>(
    cacheKey,
    request
      ? () => searchProductVariantsWithPagination(request)
      : () => Promise.resolve({ data: { content: [] } } as any),
    {
      refreshInterval: 0,
      revalidateOnFocus: false,
    }
  );
}

// Hook for getting product variants (lazy loading)
export function useProductVariants(
  productId: string | null,
  enabled: boolean = true
) {
  const cacheKey = enabled && productId
    ? `/products/${productId}/variants`
    : null;

  return useCollectionData<VariantWithRelationResponse[]>(
    cacheKey,
    enabled && productId
      ? () => getProductVariants(productId)
      : () => Promise.resolve({
        data: [],
        success: true,
        status: 200,
        statusText: "OK",
      } as ApiResponse<VariantWithRelationResponse[]>),
    {
      refreshInterval: 0,
      revalidateOnFocus: false,
    }
  );
}

// Utility functions for product operations
export const productService = {
  // Filter products by search query (client-side)
  filterProducts: (products: Product[], query: string): Product[] => {
    if (!query.trim()) return products;

    const lowerQuery = query.toLowerCase();
    return products.filter(
      (product) =>
        product.name.toLowerCase().includes(lowerQuery) ||
        product.code.toLowerCase().includes(lowerQuery) ||
        product.description?.toLowerCase().includes(lowerQuery) ||
        product.saleCode?.toLowerCase().includes(lowerQuery)
    );
  },

  // Get product statistics
  getProductStats: (products: Product[]) => {
    const totalProducts = products.length;
    const activeProducts = products.filter((p) => p.isActive).length;
    const inactiveProducts = totalProducts - activeProducts;

    return {
      totalProducts,
      activeProducts,
      inactiveProducts,
      totalValue: products.reduce((sum, p) => sum + (p.price || 0), 0),
      averagePrice:
        totalProducts > 0
          ? products.reduce((sum, p) => sum + (p.price || 0), 0) / totalProducts
          : 0,
    };
  },

  // Format product for display
  formatProduct: (product: Product) => ({
    ...product,
    displayPrice: `$${product.price.toFixed(2)}`,
    displayCost: product.cost ? `$${product.cost.toFixed(2)}` : "N/A",
    statusText: product.isActive ? "Active" : "Inactive",
  }),
};
